import { DataGlueGraph, DataConnection, FunctionDefinition } from '@glueapp/graph-types';
import { ValidationRule, ValidationRuleResult, ValidationError } from '../ValidationRule';

export const CircularConnectionRule: ValidationRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Check main graph for circular connections
    const mainGraphCycles = detectCircularConnections(graph.connections, 'main');
    errors.push(...mainGraphCycles);

    // Check each function for circular connections
    if (graph.functions) {
        for (const func of graph.functions) {
            const functionCycles = detectCircularConnections(func.connections, func.id);
            errors.push(...functionCycles);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function detectCircularConnections(connections: DataConnection[], context: string): ValidationError[] {
    const errors: ValidationError[] = [];
    const adjacencyList = new Map<number, Set<number>>();

    // Build adjacency list
    for (const connection of connections) {
        if (!adjacencyList.has(connection.sendingNodeInstanceId)) {
            adjacencyList.set(connection.sendingNodeInstanceId, new Set());
        }
        adjacencyList.get(connection.sendingNodeInstanceId)!.add(connection.receivingNodeInstanceId);
    }

    // Track visited nodes and recursion stack for cycle detection
    const visited = new Set<number>();
    const recursionStack = new Set<number>();

    // Helper function to detect cycles using DFS
    function hasCycle(nodeId: number, path: number[] = []): { found: boolean; cycle: number[] } {
        visited.add(nodeId);
        recursionStack.add(nodeId);
        path.push(nodeId);

        const neighbors = adjacencyList.get(nodeId);
        if (neighbors) {
            for (const neighbor of neighbors) {
                if (!visited.has(neighbor)) {
                    const result = hasCycle(neighbor, [...path]);
                    if (result.found) {
                        return result;
                    }
                } else if (recursionStack.has(neighbor)) {
                    // Found a cycle
                    const cycleStart = path.indexOf(neighbor);
                    return {
                        found: true,
                        cycle: [...path.slice(cycleStart), neighbor],
                    };
                }
            }
        }

        recursionStack.delete(nodeId);
        return { found: false, cycle: [] };
    }

    // Check each node that hasn't been visited
    for (const nodeId of adjacencyList.keys()) {
        if (!visited.has(nodeId)) {
            const result = hasCycle(nodeId);
            if (result.found) {
                const location = context === 'main' ? { context: 'main graph' } : { functionId: context };

                errors.push({
                    code: 'CIRCULAR_CONNECTION',
                    message: `Circular connection detected: nodes ${result.cycle.join(' → ')}`,
                    severity: 'error',
                    location,
                });
            }
        }
    }

    return errors;
}
